/* eslint-disable import/namespace */
import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { formatNumber } from '~/utils/helpers';
import Legend from './Legend';
import { ClimateCheckFootprintSettings, Co2FootprintSettings, DashboardClimaChType, DashboardCO2Type } from '~/api/services/dashboard';
import { Nullable } from '~/types';
import { Header, Segment } from 'semantic-ui-react';
import { SvgWrapper } from './style';
import InfoPopup from '../InfoPopup';

interface Props {
  min: number;
  max: number;
  co2Data: Nullable<DashboardCO2Type | DashboardClimaChType>;
  legend?: boolean;
  co2FootprintSettings: Nullable<ClimateCheckFootprintSettings | Co2FootprintSettings>;
  footprintColor: string
  dairyLabel: string
  isBayerLand?: boolean
}

const SpeedometerGraph: React.FC<Props> = (props) => {
  const { min, max, co2Data, co2FootprintSettings, legend, dairyLabel, footprintColor, isBayerLand } = props
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const displayDefaultValue = co2FootprintSettings?.showLflDefaultValue && isBayerLand

  const [dimensions, setDimensions] = useState({ width: 500, height: 250, radius: 200 });

  const updateSize = () => {
    if (containerRef.current) {
      const newWidth = containerRef.current.clientWidth > 500 ? 500 : containerRef.current.clientWidth;
      const newRadius = newWidth * 0.3;

      if (newWidth !== dimensions.width || newRadius !== dimensions.radius) {
        setDimensions({ width: newWidth, height: dimensions.height, radius: newRadius });
      }
    }
  };

  useEffect(() => {
    let animationFrameId: number;

    const debouncedUpdateSize = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(updateSize);
    };

    const observer = new ResizeObserver((entries) => {
      try {
        debouncedUpdateSize();
      } catch (error) {
        console.warn('ResizeObserver error suppressed:', error);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    updateSize();

    return () => {
      observer.disconnect();
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  useEffect(() => {
    const { width, height, radius } = dimensions;

    if (!width || !height || !radius || width <= 0 || height <= 0 || radius <= 0) {
      return;
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    const xAxisY = Math.min(height, svg.node().getBoundingClientRect().height - 1);

    const defs = svg.append('defs');

    // Radial gradient for backdrop
    const backdropGradient = defs.append('radialGradient')
      .attr('id', 'backdrop-gradient')
      .attr('cx', '50%')
      .attr('cy', '50%')
      .attr('r', '50%')
      .attr('fx', '50%')
      .attr('fy', '50%');

    backdropGradient.append('stop')
      .attr('offset', '20%')
      .attr('stop-color', '#f8fafc');

    backdropGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#f1f5f9');

    // Add backdrop circle
    svg.append('circle')
      .attr('cx', width / 2)
      .attr('cy', height)
      .attr('r', radius * 1.3 - 27)
      .attr('fill', 'url(#backdrop-gradient)')
      .attr('opacity', 0.7);

    svg.append('line')
      .attr('x1', width / 2)
      .attr('y1', height)
      .attr('x2', width / 2)
      .attr('y2', height - radius * 1.2)
      .attr('stroke', 'black')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('opacity', 0)
      .transition()
      .duration(400)
      .delay(200)
      .style('opacity', 1);

    svg.append('line')
      .attr('x1', Math.max(0, width / 2 - radius * 1.2))
      .attr('y1', xAxisY)
      .attr('x2', Math.min(width, width / 2 + radius * 1.2))
      .attr('y2', xAxisY)
      .attr('stroke', 'black')
      .attr('stroke-width', 1)
      .style('opacity', 0)
      .transition()
      .duration(200)
      .delay(400)
      .style('opacity', 1);

    const safeMin = isNaN(min) || !isFinite(min) ? 0 : min;
    const safeMax = isNaN(max) || !isFinite(max) || max <= safeMin ? safeMin + 1 : max;
    const scale = d3.scaleLinear().domain([safeMin, safeMax]).range([-Math.PI / 2, Math.PI / 2]);

    const farmerArc = d3.arc()
      .innerRadius(radius - 30)
      .outerRadius(radius - 5)
      .startAngle(-Math.PI / 2)
      .cornerRadius(5);

    const dairyArc = d3.arc()
      .innerRadius(radius - 65)
      .outerRadius(radius - 40)
      .startAngle(-Math.PI / 2)
      .cornerRadius(5);

    const defaultArc = d3.arc()
      .innerRadius(radius - 100)
      .outerRadius(radius - 75)
      .startAngle(-Math.PI / 2)
      .cornerRadius(5);

    const tooltip = d3.select('body').append('div')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', '#fff')
      .style('border', '1px solid #ddd')
      .style('padding', '5px')
      .style('border-radius', '3px')
      .style('font-size', '12px')
      .style('pointer-events', 'none');

    const farmerArcPath = svg.append('path')
      .attr('fill', co2FootprintSettings?.yourValueColor)
      .attr('transform', `translate(${width / 2}, ${height})`)
      .on('mouseover', () => {
        tooltip.style('visibility', 'visible').html(
          `Mein Betrieb: ${formatNumber(
            co2Data?.avgResultFarmer?.value ?? 0,
            2
          )} kg CO<sub>2</sub>-Äq./kg Milch`
        );
      })
      .on('mousemove', (event) => {
        tooltip.style('top', (event.pageY - 10) + 'px').style('left', (event.pageX + 10) + 'px');
      })
      .on('mouseout', () => {
        tooltip.style('visibility', 'hidden');
      });

    const dairyArcPath = svg.append('path')
      .attr('fill', displayDefaultValue ? co2FootprintSettings?.dairyValueColor : footprintColor)
      .attr('transform', `translate(${width / 2}, ${height})`)
      .on('mouseover', () => {
        tooltip.style('visibility', 'visible').html(`${dairyLabel || 'Molkerei Ø'}: ${formatNumber(co2Data?.avgDairyResults ?? 0, 2)} kg CO<sub>2</sub>-Äq./kg Milch`);
      })
      .on('mousemove', (event) => {
        tooltip
          .style('top', (event.pageY - 10) + 'px')
          .style('left', (event.pageX + 10) + 'px')
      })
      .on('mouseout', () => {
        tooltip.style('visibility', 'hidden');
      });

    const defaultArcPath = isBayerLand ? svg.append('path')
      .attr('fill', footprintColor)
      .attr('transform', `translate(${width / 2}, ${height})`)
      .on('mouseover', () => {
        tooltip.style('visibility', 'visible').html(`Bayern IDB: ${formatNumber(co2Data?.defaultResult ?? 0, 2)} kg CO<sub>2</sub>-Äq./kg Milch`);
      })
      .on('mousemove', (event) => {
        tooltip.style('top', (event.pageY - 10) + 'px').style('left', (event.pageX + 10) + 'px');
      })
      .on('mouseout', () => {
        tooltip.style('visibility', 'hidden');
      }) : null

    farmerArcPath.transition()
      .delay(500)
      .duration(800)
      .attrTween('d', function () {
        const value = co2Data?.avgResultFarmer?.value ?? safeMin;
        const interpolate = d3.interpolate(-Math.PI / 2, scale(value));
        return function (t) {
          return farmerArc.endAngle(interpolate(t))();
        };
      })

    dairyArcPath.transition()
      .delay(500)
      .duration(800)
      .attrTween('d', function () {
        const value = co2Data?.avgDairyResults ?? safeMin;
        const interpolate = d3.interpolate(-Math.PI / 2, scale(value));
        return function (t) {
          return dairyArc.endAngle(interpolate(t))();
        };
      });

    defaultArcPath?.transition()
      .delay(600)
      .duration(1000)
      .attrTween('d', function () {
        const value = co2Data?.defaultResult ?? safeMin;
        const interpolate = d3.interpolate(-Math.PI / 2, scale(value));
        return function (t) {
          return defaultArc.endAngle(interpolate(t))();
        };
      });

    const tickValues = d3.range(safeMin, safeMax + (safeMax - safeMin) / 5, (safeMax - safeMin) / 5);
    const scaleTicks = d3.scaleLinear().domain([safeMin, safeMax]).range([Math.PI, 2 * Math.PI]);
    tickValues.forEach((tick, index) => {
      const angle = scaleTicks(tick);
      const rightSide = angle > Math.PI + Math.PI / 2;
      const x = width / 2 + Math.cos(angle) * radius;
      const y = height + Math.sin(angle) * radius;
      const tickMargin = rightSide ? 50 : -70;
      if (index === 0 || index + 1 === tickValues.length) {
        svg.append('text')
          .attr('x', x + tickMargin)
          .attr('y', y - 3)
          .text(formatNumber(tick ?? tick.toFixed(2), 2))
          .attr('font-size', '14px');
      }
    });

  }, [co2Data?.avgResultFarmer, co2Data?.defaultResult, min, max, dimensions]);

  const defaultLegendItem = displayDefaultValue ?
    [{ color:footprintColor, label: 'Bayern IDB', value: co2Data?.defaultResult }] :
    []

  const legendItems = [
    { color: co2FootprintSettings?.yourValueColor, label: 'Mein Betrieb', value: co2Data?.avgResultFarmer?.value },
    {
      color: displayDefaultValue ? co2FootprintSettings?.dairyValueColor : footprintColor,
      label: dairyLabel || 'Molkerei Ø',
      value: co2Data?.avgDairyResults
    },
    ...defaultLegendItem
  ]

  return (
    <div ref={containerRef} style={{
      width: '40%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: 200,
      maxWidth: 500
    }}>
      <SvgWrapper >
        <Header attached='top' block>{'THG-Emissionen'}</Header>
        <Segment attached style={{ borderRadius: 5, position: 'relative' }}>
          <div style={{ position: 'absolute', right: 10, top: 10 }}>
            <InfoPopup
              styleIcon={{ fontSize: '1.4em' }}
              position={'top center'}
              content={'Diese Grafik zeigt den Wert der Treibhausgasemissionen für Ihren Betrieb aus der letzten abgeschlossenen Datenerhebung (“Mein Betrieb”) im Vergleich zum Durchschnitt aller erfassten Betriebe Ihrer Molkerei.'}
            />
          </div>
          <svg ref={svgRef} width={dimensions.width} height={dimensions.height} />
          {legend && <Legend items={legendItems} />}
        </Segment>
      </SvgWrapper>
    </div>
  );
};

export default SpeedometerGraph;
